import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { Brain, Sparkles, RefreshCw, Zap, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import MobileNav from '@/components/MobileNav';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AI_MODELS = [
  {
    id: 'openai',
    name: 'GPT-5',
    provider: 'OpenAI',
    description: 'Le plus puissant pour l\'analyse financière structurée',
    color: 'from-green-500 to-emerald-600',
    border: 'border-green-500/40',
    bg: 'bg-green-500/10',
    text: 'text-green-400',
    icon: '⚡',
  },
  {
    id: 'anthropic',
    name: 'Claude 4 Sonnet',
    provider: 'Anthropic',
    description: 'Excellence en raisonnement et conseils nuancés',
    color: 'from-orange-500 to-amber-600',
    border: 'border-orange-500/40',
    bg: 'bg-orange-500/10',
    text: 'text-orange-400',
    icon: '🧠',
  },
  {
    id: 'gemini',
    name: 'Gemini 2.5 Pro',
    provider: 'Google',
    description: 'Analyse multi-sources avec données en temps réel',
    color: 'from-blue-500 to-cyan-600',
    border: 'border-blue-500/40',
    bg: 'bg-blue-500/10',
    text: 'text-blue-400',
    icon: '✦',
  },
  {
    id: 'auto',
    name: 'Auto',
    provider: 'Rotation',
    description: 'Sélection automatique du meilleur modèle disponible',
    color: 'from-purple-500 to-pink-600',
    border: 'border-purple-500/40',
    bg: 'bg-purple-500/10',
    text: 'text-purple-400',
    icon: '🔄',
  },
];

const MODEL_DISPLAY_NAMES = {
  'GPT-5 (OpenAI)': 'openai',
  'Claude 4 Sonnet (Anthropic)': 'anthropic',
  'Gemini 2.5 Pro (Google)': 'gemini',
};

function getModelInfo(modelUsed) {
  const key = Object.keys(MODEL_DISPLAY_NAMES).find(k => modelUsed?.includes(k.split(' ')[0]));
  const id = MODEL_DISPLAY_NAMES[key] || 'auto';
  return AI_MODELS.find(m => m.id === id) || AI_MODELS[3];
}

export default function Recommendations() {
  const { token } = useAuth();
  const [recommendations, setRecommendations] = useState([]);
  const [selectedModel, setSelectedModel] = useState('openai');
  const [loading, setLoading] = useState(false);
  const [loadingModel, setLoadingModel] = useState(null);

  useEffect(() => {
    fetchRecommendations();
  }, []);

  const fetchRecommendations = async () => {
    try {
      const res = await axios.get(`${API}/recommendations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Keep only latest per model
      const seen = new Set();
      const unique = res.data.filter(r => {
        const key = r.model_used;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
      setRecommendations(unique);
    } catch (err) {
      console.error('Failed to fetch recommendations:', err);
    }
  };

  const generateRecommendation = async (modelId) => {
    setLoadingModel(modelId);
    setLoading(true);
    try {
      const res = await axios.post(
        `${API}/recommendations/generate`,
        { model_choice: modelId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const model = AI_MODELS.find(m => m.id === modelId);
      toast.success(`Recommandation ${model?.name} générée !`);
      await fetchRecommendations();
    } catch (err) {
      toast.error('Erreur de génération');
    } finally {
      setLoading(false);
      setLoadingModel(null);
    }
  };

  const getRecommendation = (modelId) => {
    if (modelId === 'auto') return null;
    return recommendations.find(r => {
      const info = getModelInfo(r.model_used);
      return info.id === modelId;
    });
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <MobileNav currentPage="recommendations" />

      <div className="max-w-5xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-10 animate-slide-up">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#ff6b35] to-[#ff8555] flex items-center justify-center">
              <Brain className="w-5 h-5 text-[#0a0a0a]" />
            </div>
            <span className="text-[#ff6b35] font-semibold text-sm uppercase tracking-widest">Recommandations IA</span>
          </div>
          <h1 className="text-4xl font-bold mb-2">Choisissez votre <span className="gradient-text">conseiller IA</span></h1>
          <p className="text-gray-400">Obtenez des recommandations personnalisées basées sur votre portfolio</p>
        </div>

        {/* AI Model Selector */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          {AI_MODELS.map(model => {
            const isSelected = selectedModel === model.id;
            const rec = getRecommendation(model.id);
            const isLoading = loadingModel === model.id;

            return (
              <button
                key={model.id}
                data-testid={`model-selector-${model.id}`}
                onClick={() => setSelectedModel(model.id)}
                className={`relative p-4 rounded-2xl border transition-all text-left ${
                  isSelected
                    ? `${model.border} ${model.bg}`
                    : 'border-gray-800 hover:border-gray-600 bg-gray-900/30'
                }`}
              >
                {rec && (
                  <div className="absolute top-2 right-2">
                    <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center">
                      <Check className="w-3 h-3 text-white" />
                    </div>
                  </div>
                )}
                <div className="text-2xl mb-2">{model.icon}</div>
                <div className={`font-bold text-sm ${isSelected ? model.text : 'text-white'}`}>{model.name}</div>
                <div className="text-xs text-gray-500 mt-0.5">{model.provider}</div>
                {isSelected && (
                  <div className={`mt-2 text-xs ${model.text} font-semibold`}>Sélectionné</div>
                )}
              </button>
            );
          })}
        </div>

        {/* Selected Model Panel */}
        {AI_MODELS.filter(m => m.id === selectedModel).map(model => {
          const rec = getRecommendation(model.id);
          const isLoading = loadingModel === model.id;

          return (
            <div key={model.id} className={`glass rounded-2xl border ${model.border} mb-8 overflow-hidden`}>
              {/* Model Header */}
              <div className={`p-6 border-b border-gray-800/50`}>
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-xl ${model.bg} flex items-center justify-center text-2xl`}>
                      {model.icon}
                    </div>
                    <div>
                      <h2 className={`text-xl font-bold ${model.text}`}>{model.name}</h2>
                      <p className="text-gray-400 text-sm">{model.description}</p>
                    </div>
                  </div>
                  <Button
                    data-testid={`generate-btn-${model.id}`}
                    onClick={() => generateRecommendation(model.id)}
                    disabled={loading}
                    className={`bg-gradient-to-r ${model.color} text-white font-bold px-6`}
                  >
                    {isLoading ? (
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Sparkles className="w-4 h-4 mr-2" />
                    )}
                    {isLoading ? 'Génération...' : rec ? 'Actualiser' : 'Générer'}
                  </Button>
                </div>
              </div>

              {/* Recommendation Content */}
              <div className="p-6">
                {rec ? (
                  <div>
                    <div className="flex items-center gap-2 text-xs text-gray-500 mb-4">
                      <Zap className="w-3 h-3" />
                      <span>Généré le {new Date(rec.created_at).toLocaleDateString('fr-FR', {
                        day: 'numeric', month: 'long', year: 'numeric',
                        hour: '2-digit', minute: '2-digit'
                      })}</span>
                    </div>
                    <div className="text-gray-300 leading-relaxed whitespace-pre-wrap">
                      {rec.content}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-10">
                    <div className="text-4xl mb-4">{model.icon}</div>
                    <p className="text-gray-400 mb-2">Aucune recommandation {model.name}</p>
                    <p className="text-gray-600 text-sm">Cliquez sur "Générer" pour obtenir un conseil personnalisé</p>
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {/* All Recommendations Summary */}
        {recommendations.length > 0 && (
          <div>
            <h2 className="text-lg font-bold mb-4 text-gray-400">Toutes mes recommandations</h2>
            <div className="space-y-4">
              {recommendations.map((rec, idx) => {
                const info = getModelInfo(rec.model_used);
                return (
                  <div
                    key={rec.id}
                    data-testid={`recommendation-card-${idx}`}
                    className={`glass rounded-xl p-5 border ${info.border} cursor-pointer hover:opacity-80 transition-all`}
                    onClick={() => setSelectedModel(info.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="text-xl">{info.icon}</span>
                        <div>
                          <div className={`font-semibold text-sm ${info.text}`}>{rec.model_used}</div>
                          <div className="text-xs text-gray-500">
                            {new Date(rec.created_at).toLocaleDateString('fr-FR', {
                              day: 'numeric', month: 'long', year: 'numeric'
                            })}
                          </div>
                        </div>
                      </div>
                      <Button
                        onClick={(e) => { e.stopPropagation(); generateRecommendation(info.id); }}
                        disabled={loading}
                        size="sm"
                        variant="outline"
                        className={`border-gray-700 ${info.text} text-xs`}
                      >
                        <RefreshCw className="w-3 h-3 mr-1" />
                        Actualiser
                      </Button>
                    </div>
                    <p className="text-gray-400 text-sm mt-3 line-clamp-2">{rec.content}</p>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
